import { get, httpDelete, patch, post, uploadFile } from "@readai/common/api";
import type { WorkspaceSearchResponse } from "@readai/common/types";
import type {
  ApiEnterpriseWorkspace,
  EnterpriseWorkspace,
  EnterpriseWorkspaceSettings,
  UpdateWorkspaceSettingsBody,
  WorkspaceSearchParams,
} from "@readai/common/types/workspaceTypes";
import { withQueryParamsUrl } from "@readai/common/utils";
import { snakeKeys } from "@readai/common/utils/formatUtils";

import * as domains from "./domains";
import * as invites from "./invites";
import * as samlSettings from "./samlSettings";
import * as teams from "./teams";
import * as users from "./users";

const getWorkspace = async (
  workspaceId: string,
): Promise<ApiEnterpriseWorkspace> => {
  const workspace: ApiEnterpriseWorkspace = await get(
    `/enterprise/workspaces/${workspaceId}`,
    { withCreds: true },
  );
  // un-camelize the user integration counts since we want the keys to
  // match our known provider ids
  if (workspace.userIntegrationCounts) {
    workspace.userIntegrationCounts = snakeKeys(
      workspace.userIntegrationCounts,
    );
  }
  return workspace;
};

const createWorkspace = async (
  name: string,
  settings?: Partial<EnterpriseWorkspaceSettings>,
): Promise<ApiEnterpriseWorkspace> => {
  return post(
    "/enterprise/workspaces",
    { name, settings },
    { withCreds: true },
  );
};

const deleteWorkspace = async (workspaceId: string): Promise<void> => {
  return httpDelete(`/enterprise/workspaces/${workspaceId}`, {
    withCreds: true,
  });
};

const patchWorkspace = async (
  workspaceId: string,
  body: Partial<EnterpriseWorkspace>,
): Promise<void> =>
  patch(`/enterprise/workspaces/${workspaceId}`, body, {
    withCreds: true,
  });

const updateWorkspaceSettings = async (
  workspaceId: string,
  settings: UpdateWorkspaceSettingsBody,
): Promise<void> => {
  return patch(
    `/enterprise/workspaces/${workspaceId}`,
    { settings },
    { withCreds: true },
  );
};

const uploadCustomLogo = async (
  workspaceId: string,
  file: File,
): Promise<void> =>
  uploadFile(`/enterprise/workspaces/${workspaceId}/custom-bot-logo`, file, {
    withCreds: true,
  });

const deleteCustomLogo = async (workspaceId: string): Promise<void> =>
  httpDelete(`/enterprise/workspaces/${workspaceId}/custom-bot-logo`, {
    withCreds: true,
  });

export const searchWorkspace = async (
  workspaceId: string,
  params: WorkspaceSearchParams,
): Promise<WorkspaceSearchResponse> => {
  const url = withQueryParamsUrl(
    `/enterprise/workspaces/${workspaceId}/search`,
    { query: params.query, include_entity: params.includeEntities },
  );
  const json = await post(url, {}, { withCreds: true });
  return json as WorkspaceSearchResponse;
};

export default {
  getWorkspace,
  createWorkspace,
  patchWorkspace,
  deleteWorkspace,
  updateWorkspaceSettings,
  uploadCustomLogo,
  deleteCustomLogo,
  searchWorkspace,
  domains,
  invites,
  samlSettings,
  teams,
  users,
};
